import React from 'react';
import styled from 'styled-components';
import * as numeral from 'numeral';
import { Button } from '@lendi-ui/button';
import Field from '@lendi-ui/field';
import { Calendar, KeyboardArrowLeft, Message } from '@lendi-ui/icon';
import { Input } from '@lendi-ui/text-input';
import { Body, Heading } from '@lendi-ui/typography';
import { useCommPanel } from '@lendi/navbar';
import {
  calculateAccessibleEquityValue,
  calculateEquityValue,
  calculatePrincipalInterestMonthlyRepayment,
  eventTracking,
  TextInputAttrs,
  useTextInput,
} from '../../utils';
import {
  CalculateButton,
  CtaSection,
  InputExtra,
  InputGroup,
  MoreCtas,
  PropertyReportLink,
  QuoteAmount,
  QuotePeriod,
  QuoteSection,
  ResetButton,
  ResultsWrapper,
  Title,
  Wrapper,
} from './index.style';

const CalculateEquityButton = styled(CalculateButton)`
  min-width: 16rem;
`;

interface InputsViewProps {
  propertyValue: TextInputAttrs;
  outstandingLoanAmount: TextInputAttrs;
  calculateEquity(): void;
}
const InputsView = ({ propertyValue, outstandingLoanAmount, calculateEquity }: InputsViewProps) => (
  <form
    onSubmit={(e) => {
      e.preventDefault();
      calculateEquity();
    }}
  >
    <Title>How much equity can you access?</Title>
    <InputGroup>
      <Field size="sm" label="My property is worth approximately">
        <Input {...propertyValue} before={<InputExtra side="left">$</InputExtra>} isFullWidth={true} />
      </Field>
      <Body size="md" style={{ minWidth: '16rem' }}>
        Don't know your property value?
        <br />
        <PropertyReportLink
          href="/property-report"
          onClick={() => {
            eventTracking('above-fold', 'navigate', 'Get a free property report');
          }}
        >
          Get a free property report
        </PropertyReportLink>
      </Body>
    </InputGroup>
    <InputGroup>
      <Field size="sm" label="Outstanding home loan amount">
        <Input {...outstandingLoanAmount} before={<InputExtra side="left">$</InputExtra>} isFullWidth={true} />
      </Field>
      <CalculateEquityButton
        type="submit"
        variant="primary"
        isDisabled={propertyValue.value === '0' || outstandingLoanAmount.value === '0.0'}
        onClick={() => {
          eventTracking('above-fold', 'navigate', 'Calculate equity');
        }}
      >
        Calculate equity
      </CalculateEquityButton>
    </InputGroup>
  </form>
);

interface ResultsViewProps {
  accessibleEquityLimit: Numeral;
  monthlyRepayment: Numeral;
  interestRate: Numeral;
  loanTerm: Numeral;
  resetCalculator(): void;
}
const ResultsView = ({
  accessibleEquityLimit,
  monthlyRepayment,
  interestRate,
  loanTerm,
  resetCalculator,
}: ResultsViewProps) => {
  const { openChat } = useCommPanel();
  return (
    <>
      <ResetButton onClick={resetCalculator}>
        <KeyboardArrowLeft color="primary.500" /> Start again
      </ResetButton>
      <ResultsWrapper>
        <QuoteSection style={{ minWidth: '22rem' }}>
          <Title>You could access up to</Title>
          <QuoteAmount>$ {accessibleEquityLimit.format()}</QuoteAmount>
          <Heading size="xs" mt="sm" mb="xxs">
            Your new repayments could be
          </Heading>
          <QuoteAmount>$ {monthlyRepayment.format()}</QuoteAmount>
          <QuotePeriod>/ month</QuotePeriod>
          <Body size="sm" mt="xs">
            At a variable rate of {interestRate.format('0.00')}% on a loan term of {loanTerm.format()} years
          </Body>
          <Body size="xs" mt="md" color="shade.500">
            <strong>Disclaimer:</strong> While every effort has been made to ensure the accuracy of this calculator, the
            actual results may vary depending on a variety of factors. This result is an indication only.
          </Body>
        </QuoteSection>
        <CtaSection>
          <Heading size="xs" mt="nil" mb="xs">
            Find out which cash out loans you could qualify for in 30 seconds
          </Heading>
          <Body size="sm" mb="md">
            Choose from over 30 major lenders and get approved quickly and easy.
          </Body>
          <Button
            variant="primary"
            isFullWidth={true}
            href="/home-loans/search"
            onClick={() => {
              eventTracking('above-fold', 'navigate', 'Compare now');
            }}
          >
            Compare now
          </Button>
          <MoreCtas>
            <Button
              variant="empty"
              size="sm"
              onClick={() => {
                openChat();
                eventTracking('above-fold', 'navigate', 'Chat now');
              }}
            >
              <Message color="primary.500" /> Chat now
            </Button>
            <Button
              variant="empty"
              size="sm"
              href="/appointment-booking/postcode"
              onClick={() => {
                eventTracking('above-fold', 'navigate', 'Book an appointment');
              }}
            >
              <Calendar color="primary.500" /> Book an appointment
            </Button>
          </MoreCtas>
        </CtaSection>
      </ResultsWrapper>
    </>
  );
};

export const EquityCalculator = () => {
  const propertyValue = useTextInput(numeral(1000000), numeral, (v) => v.format());
  const outstandingLoanAmount = useTextInput(numeral(400000), numeral, (v) => v.format());
  const [accessibleEquityLimit, setAccessibleEquityLimit] = React.useState(0);
  const [monthlyRepayment, setMonthlyRepayment] = React.useState(0);
  const [isResultsView, setIsResultsView] = React.useState(false);
  const interestRate = 3.69;
  const loanTerm = 25;

  function resetCalculator() {
    propertyValue.update(numeral(1000000));
    outstandingLoanAmount.update(numeral(400000));
    setAccessibleEquityLimit(0);
    setMonthlyRepayment(0);
    setIsResultsView(false);
  }

  function calculateEquity() {
    // Calculate the accesible equity value
    const equityValue = calculateEquityValue(propertyValue.value.value());
    const accessibleEquityLimit = calculateAccessibleEquityValue(outstandingLoanAmount.value.value(), equityValue);
    setAccessibleEquityLimit(accessibleEquityLimit);

    // Calculate principal and interest monthly payment
    const newLoanAmount = outstandingLoanAmount.value.value() + accessibleEquityLimit;
    setMonthlyRepayment(calculatePrincipalInterestMonthlyRepayment(newLoanAmount, interestRate, loanTerm));

    setIsResultsView(true);
  }

  return (
    <Wrapper>
      {isResultsView ? (
        <ResultsView
          accessibleEquityLimit={numeral(accessibleEquityLimit)}
          monthlyRepayment={numeral(monthlyRepayment)}
          interestRate={numeral(interestRate)}
          loanTerm={numeral(loanTerm)}
          resetCalculator={resetCalculator}
        />
      ) : (
        <InputsView
          propertyValue={propertyValue.inputAttrs}
          outstandingLoanAmount={outstandingLoanAmount.inputAttrs}
          calculateEquity={calculateEquity}
        />
      )}
    </Wrapper>
  );
};
